import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Calculator, Info, MapPin, Truck, ChevronDown, ChevronUp, AlertTriangle, CheckCircle } from 'lucide-react';
import { RAIL_RAMPS } from '@/constants/columbusTerminals';
import { CONTAINER_SIZES } from '@/constants/containerTypes';
import { ACCESSORIAL_CHARGES, OVERWEIGHT_THRESHOLD, calculateTotalCharges, MAX_CONTAINERS } from '@/constants/commercialLogic';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const RateCalculator = () => {
  // Form state
  const [ramp, setRamp] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [numberOfContainers, setNumberOfContainers] = useState(1);
  const [containers, setContainers] = useState([{
    id: 1,
    container_size: "40' Dry",
    weight_lbs: '',
    hazmat: false,
    chassis_required: false
  }]);
  
  // Rate calculation state
  const [calculating, setCalculating] = useState(false);
  const [rateData, setRateData] = useState(null);
  const [error, setError] = useState(null);
  
  // Additional accessorials state
  const [selectedAccessorials, setSelectedAccessorials] = useState({});
  const [storageDays, setStorageDays] = useState(1);
  const [detentionHours, setDetentionHours] = useState(0);
  
  // UI state
  const [showChargeDetails, setShowChargeDetails] = useState(true);

  // Update containers array when number changes
  useEffect(() => {
    const currentCount = containers.length;
    if (numberOfContainers > currentCount) {
      const newContainers = [...containers];
      for (let i = currentCount; i < numberOfContainers; i++) {
        newContainers.push({
          id: i + 1,
          container_size: "40' Dry",
          weight_lbs: '',
          hazmat: false,
          chassis_required: false
        });
      }
      setContainers(newContainers);
    } else if (numberOfContainers < currentCount) {
      setContainers(containers.slice(0, numberOfContainers));
    }
  }, [numberOfContainers]);

  // Reset rate data when inputs change
  useEffect(() => {
    setRateData(null);
    setError(null);
  }, [ramp, city, zip]);

  // Update a specific container
  const updateContainer = (index, field, value) => {
    const newContainers = [...containers];
    newContainers[index] = { ...newContainers[index], [field]: value };
    setContainers(newContainers);
  };

  // Calculate rate
  const calculateRate = async () => {
    if (!ramp || !city || !zip) {
      setError('Please select a ramp and enter city and ZIP code');
      return;
    }

    setCalculating(true);
    setError(null);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/rate/calculate`, {
        ramp,
        city,
        zip,
        container_size: containers[0]?.container_size || "40' Dry"
      });
      
      setRateData(response.data);
    } catch (err) {
      console.error('Rate calculation error:', err);
      setError(err.response?.data?.detail || 'Failed to calculate rate. Please try again.');
    } finally {
      setCalculating(false);
    }
  };

  // Calculate charges breakdown
  const chargesBreakdown = useMemo(() => {
    if (!rateData) return null;
    
    const additionalAcc = {};
    Object.keys(selectedAccessorials).forEach(key => {
      if (selectedAccessorials[key]) {
        if (key === 'storage') {
          additionalAcc[key] = { days: storageDays };
        } else if (key === 'detention_port' || key === 'detention_receiver') {
          additionalAcc[key] = { hours: detentionHours };
        } else {
          additionalAcc[key] = true;
        }
      }
    });
    
    return calculateTotalCharges({
      containers,
      lineHaul: rateData.line_haul,
      zone: rateData.zone,
      additionalAccessorials: additionalAcc
    });
  }, [rateData, containers, selectedAccessorials, storageDays, detentionHours]);

  const toggleAccessorial = (key) => {
    setSelectedAccessorials(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="bg-[#0F172A] border border-white/10 rounded-lg p-6 md:p-8" data-testid="rate-calculator">
      <div className="flex items-center gap-3 mb-6">
        <Calculator className="text-[#00A6E8]" size={32} />
        <h2 className="text-2xl md:text-3xl font-bold uppercase text-white">Rate Calculator</h2>
      </div>

      <div className="bg-[#00A6E8]/10 border border-[#00A6E8]/20 rounded-lg p-4 mb-6 flex items-start gap-3">
        <Info className="text-[#00A6E8] flex-shrink-0 mt-1" size={20} />
        <div className="text-sm text-slate-300">
          <p className="font-semibold text-white mb-1">DLT Rate Profile</p>
          <p>Enter pickup ramp and delivery location. Accessorial charges are calculated automatically based on your selections.</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Step 1: Location Selection */}
        <div className="bg-[#1E293B]/50 rounded-lg p-4 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="text-[#FFA726]" size={20} />
            <h3 className="text-white font-semibold">Step 1: Select Locations</h3>
          </div>
          
          <div>
            <Label className="text-slate-300 mb-2 block">Pickup/Delivery Ramp *</Label>
            <Select value={ramp} onValueChange={setRamp}>
              <SelectTrigger className="bg-[#1E293B] border-white/10 text-white" data-testid="ramp-select">
                <SelectValue placeholder="Select Rail Ramp" />
              </SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-white/10 text-white">
                {RAIL_RAMPS.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300 mb-2 block">Delivery City *</Label>
              <Input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Enter city"
                className="bg-[#1E293B] border-white/10 text-white"
                data-testid="rate-city-input"
              />
            </div>
            <div>
              <Label className="text-slate-300 mb-2 block">ZIP Code *</Label>
              <Input
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder="Enter ZIP"
                className="bg-[#1E293B] border-white/10 text-white"
                data-testid="rate-zip-input"
              />
            </div>
          </div>
        </div>

        {/* Step 2: Container Details */}
        <div className="bg-[#1E293B]/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <Truck className="text-[#FFA726]" size={20} />
            <h3 className="text-white font-semibold">Step 2: Container Details</h3>
          </div>
          
          <div className="mb-4">
            <Label className="text-slate-300 mb-2 block">Number of Containers</Label>
            <Input
              type="number"
              min={1}
              max={MAX_CONTAINERS}
              value={numberOfContainers}
              onChange={(e) => setNumberOfContainers(Math.min(MAX_CONTAINERS, Math.max(1, parseInt(e.target.value) || 1)))}
              className="w-32 bg-[#1E293B] border-white/10 text-white"
              data-testid="container-count-input"
            />
            <p className="text-xs text-slate-500 mt-1">Maximum {MAX_CONTAINERS} containers</p>
          </div>
          
          {/* Container entries */}
          <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
            {containers.map((container, index) => (
              <div key={container.id} className="bg-[#0F172A] border border-white/10 rounded-lg p-4">
                <p className="text-[#00A6E8] font-semibold text-sm mb-3">
                  Container {index + 1} of {numberOfContainers}
                </p>
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-slate-400 text-xs mb-1 block">Container Size/Type</Label>
                    <Select 
                      value={container.container_size} 
                      onValueChange={(v) => updateContainer(index, 'container_size', v)}
                    >
                      <SelectTrigger className="bg-[#1E293B] border-white/10 text-white text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1E293B] border-white/10 text-white max-h-[200px]">
                        {CONTAINER_SIZES.map((size) => (
                          <SelectItem key={size} value={size}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-slate-400 text-xs mb-1 block">Weight (lbs)</Label>
                    <Input
                      type="number"
                      value={container.weight_lbs}
                      onChange={(e) => updateContainer(index, 'weight_lbs', parseInt(e.target.value) || '')}
                      placeholder="Enter weight"
                      className="bg-[#1E293B] border-white/10 text-white text-sm"
                    />
                    {container.weight_lbs > OVERWEIGHT_THRESHOLD && (
                      <p className="text-yellow-400 text-xs mt-1 flex items-center gap-1">
                        <AlertTriangle size={12} /> Overweight charge applied
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={container.hazmat}
                      onCheckedChange={(checked) => updateContainer(index, 'hazmat', checked)}
                      className="border-white/30"
                    />
                    <span className="text-slate-300 text-sm">Hazmat</span>
                    {container.hazmat && (
                      <span className="text-yellow-400 text-xs">(+$175)</span>
                    )}
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={container.chassis_required}
                      onCheckedChange={(checked) => updateContainer(index, 'chassis_required', checked)}
                      className="border-white/30"
                    />
                    <span className="text-slate-300 text-sm">Chassis Required</span>
                    {container.chassis_required && (
                      <span className="text-[#00A6E8] text-xs">(+$90 min)</span>
                    )}
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Calculate Button */}
        <Button
          onClick={calculateRate}
          disabled={calculating || !ramp || !city || !zip}
          className="w-full bg-[#FFA726] text-[#0A0E1A] font-bold uppercase tracking-wider hover:bg-[#FFB74D] py-4"
          data-testid="calculate-rate-btn"
        >
          {calculating ? 'Calculating...' : 'Calculate Rate'}
        </Button>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Rate Result */}
        {rateData && chargesBreakdown && (
          <div className="border-t border-white/10 pt-6 space-y-4">
            {/* Zone Information */}
            <div className={`rounded-lg p-4 ${
              rateData.zone === 'C' 
                ? 'bg-yellow-500/10 border border-yellow-500/20' 
                : 'bg-green-500/10 border border-green-500/20'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-semibold">Zone Classification</span>
                <span className={`font-bold text-lg ${
                  rateData.zone === 'C' ? 'text-yellow-400' : 'text-green-400'
                }`}>
                  {rateData.zone_label}
                </span>
              </div>
              <p className="text-slate-400 text-sm">
                Distance: <span className="text-white font-medium">{rateData.distance_miles} miles</span>
              </p>
            </div>

            {/* Zone C Custom Quote Message */}
            {rateData.zone === 'C' && (
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="text-yellow-400 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="text-yellow-400 font-semibold mb-2">Custom Line Haul Pricing Required</p>
                    <p className="text-slate-300 text-sm">
                      This location requires custom Line Haul pricing. Your current total reflects accessorial charges only.
                      A DLT team member will contact you shortly with the Line Haul rate and final total.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Charge Details Toggle */}
            <button
              onClick={() => setShowChargeDetails(!showChargeDetails)}
              className="flex items-center gap-2 text-[#00A6E8] hover:text-[#33B8ED] transition-colors w-full"
            >
              {showChargeDetails ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              <span className="font-semibold">View Charge Details</span>
            </button>

            {/* Charges Breakdown */}
            {showChargeDetails && (
              <div className="bg-[#1E293B]/50 rounded-lg p-4 space-y-3">
                {/* Line Haul - HIDDEN FROM CUSTOMERS */}
                {/* Line Haul will be quoted by DLT operations team */}

                {/* Auto-Applied Charges */}
                <p className="text-slate-400 text-xs uppercase tracking-wider pt-2">Auto-Applied Charges</p>
                
                {/* Hazmat */}
                {chargesBreakdown.hazmat.count > 0 && (
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="text-yellow-400" size={14} />
                      <span className="text-slate-300">HAZMAT</span>
                      <span className="text-slate-500 text-sm">
                        ({chargesBreakdown.hazmat.count} container{chargesBreakdown.hazmat.count > 1 ? 's' : ''})
                      </span>
                    </div>
                    <span className="text-yellow-400 font-medium">${chargesBreakdown.hazmat.amount}</span>
                  </div>
                )}

                {/* Overweight */}
                {chargesBreakdown.overweight.count > 0 && (
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="text-yellow-400" size={14} />
                      <span className="text-slate-300">Overweight Fee</span>
                      <span className="text-slate-500 text-sm">
                        ({chargesBreakdown.overweight.count} container{chargesBreakdown.overweight.count > 1 ? 's' : ''})
                      </span>
                    </div>
                    <span className="text-yellow-400 font-medium">${chargesBreakdown.overweight.amount}</span>
                  </div>
                )}

                {/* Chassis */}
                {chargesBreakdown.chassis.count > 0 && (
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="text-[#00A6E8]" size={14} />
                      <span className="text-slate-300">Chassis Rental (2-day min)</span>
                      <span className="text-slate-500 text-sm">
                        ({chargesBreakdown.chassis.count} container{chargesBreakdown.chassis.count > 1 ? 's' : ''})
                      </span>
                    </div>
                    <span className="text-[#00A6E8] font-medium">${chargesBreakdown.chassis.amount}</span>
                  </div>
                )}

                {/* Additional Accessorials */}
                <p className="text-slate-400 text-xs uppercase tracking-wider pt-4">Additional Accessorials</p>
                
                <div className="space-y-2">
                  {/* Pre-Pull */}
                  <label className="flex items-center justify-between cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedAccessorials.pre_pull || false}
                        onCheckedChange={() => toggleAccessorial('pre_pull')}
                        className="border-white/30"
                      />
                      <span className="text-slate-300 text-sm">Pre-Pull</span>
                    </div>
                    <span className="text-slate-400 text-sm">$115</span>
                  </label>

                  {/* Chassis Split */}
                  <label className="flex items-center justify-between cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedAccessorials.chassis_split || false}
                        onCheckedChange={() => toggleAccessorial('chassis_split')}
                        className="border-white/30"
                      />
                      <span className="text-slate-300 text-sm">Chassis Split</span>
                    </div>
                    <span className="text-slate-400 text-sm">$85</span>
                  </label>

                  {/* Stop-off */}
                  <label className="flex items-center justify-between cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedAccessorials.stop_off || false}
                        onCheckedChange={() => toggleAccessorial('stop_off')}
                        className="border-white/30"
                      />
                      <span className="text-slate-300 text-sm">Stop-off</span>
                    </div>
                    <span className="text-slate-400 text-sm">$150</span>
                  </label>

                  {/* Storage */}
                  <div>
                    <label className="flex items-center justify-between cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedAccessorials.storage || false}
                          onCheckedChange={() => toggleAccessorial('storage')}
                          className="border-white/30"
                        />
                        <span className="text-slate-300 text-sm">Storage</span>
                      </div>
                      <span className="text-slate-400 text-sm">$40/day</span>
                    </label>
                    {selectedAccessorials.storage && (
                      <div className="ml-6 mt-2 flex items-center gap-2">
                        <Label className="text-slate-500 text-xs">Days:</Label>
                        <Input
                          type="number"
                          min={1}
                          value={storageDays}
                          onChange={(e) => setStorageDays(parseInt(e.target.value) || 1)}
                          className="w-16 h-8 bg-[#1E293B] border-white/10 text-white text-sm"
                        />
                      </div>
                    )}
                  </div>

                  {/* Detention */}
                  <div>
                    <label className="flex items-center justify-between cursor-pointer">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedAccessorials.detention_port || false}
                          onCheckedChange={() => toggleAccessorial('detention_port')}
                          className="border-white/30"
                        />
                        <span className="text-slate-300 text-sm">Detention</span>
                      </div>
                      <span className="text-slate-400 text-sm">$85/hr</span>
                    </label>
                    {selectedAccessorials.detention_port && (
                      <div className="ml-6 mt-2 flex items-center gap-2">
                        <Label className="text-slate-500 text-xs">Hours:</Label>
                        <Input
                          type="number"
                          min={0}
                          value={detentionHours}
                          onChange={(e) => setDetentionHours(parseInt(e.target.value) || 0)}
                          className="w-16 h-8 bg-[#1E293B] border-white/10 text-white text-sm"
                        />
                      </div>
                    )}
                  </div>

                  {/* Drop Charge */}
                  <label className="flex items-center justify-between cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedAccessorials.drop_charge || false}
                        onCheckedChange={() => toggleAccessorial('drop_charge')}
                        className="border-white/30"
                      />
                      <span className="text-slate-300 text-sm">Drop Charge</span>
                    </div>
                    <span className="text-slate-400 text-sm">$50</span>
                  </label>

                  {/* Tri-Axle */}
                  <label className="flex items-center justify-between cursor-pointer">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedAccessorials.tri_axle_rental || false}
                        onCheckedChange={() => toggleAccessorial('tri_axle_rental')}
                        className="border-white/30"
                      />
                      <span className="text-slate-300 text-sm">Tri-Axle Rental</span>
                    </div>
                    <span className="text-slate-400 text-sm">$100/day</span>
                  </label>
                </div>

                {/* Additional accessorials total */}
                {chargesBreakdown.additional.length > 0 && (
                  <div className="border-t border-white/10 pt-3 mt-3">
                    {chargesBreakdown.additional.map((acc, i) => (
                      <div key={i} className="flex justify-between items-center">
                        <span className="text-slate-300">{acc.label}</span>
                        <span className="text-white font-medium">${acc.amount}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Total Section */}
            <div className="bg-[#0F172A] border border-[#00A6E8]/30 rounded-lg p-4">
              {rateData.zone === 'C' ? (
                <>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-slate-400">Accessorial Charges Total:</span>
                    <span className="text-2xl font-bold text-[#00A6E8]">
                      ${chargesBreakdown.subtotalAccessorials}
                    </span>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3 mt-3">
                    <p className="text-sm text-slate-400">
                      <span className="text-white font-medium">Line Haul:</span> Will be quoted by DLT operations team.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 text-lg">Accessorial Charges Total:</span>
                    <span className="text-3xl font-bold text-[#00A6E8]" data-testid="total-estimate">
                      ${chargesBreakdown.subtotalAccessorials}
                    </span>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3 mt-3">
                    <p className="text-sm text-slate-400">
                      <span className="text-white font-medium">Line Haul:</span> Will be quoted by DLT operations team.
                    </p>
                  </div>
                </>
              )}
              <p className="text-xs text-slate-500 mt-3 text-right">
                *Accessorial charges shown. Line Haul quotation provided after DLT review.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
